/* Canvello — trade application: the seller's permit (2026-10-21).
 *
 * sections/cv-trade-apply.liquid. The form is Shopify's customer form, which
 * can't carry a file, so the permit goes to the Canvello API as soon as it is
 * chosen (or dropped); the private link that comes back fills a hidden note
 * field (contact[note][Seller's permit]), so it reaches the customer record
 * and the trade desk. The field shows, and becomes required, only here:
 * without this script the form still sends, and its hint says to email the
 * certificate instead.
 *
 * On send, a copy goes to the store by email (the contact form beside it,
 * as offers do) with every answer and the link. The application itself is
 * held back while the file uploads, or when none is attached.
 */
(() => {
  const MAX = 10 * 1024 * 1024;
  const OK = /\.(pdf|jpe?g|png|webp|hei[cf])$/i;

  const size = (n) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

  function setup(box) {
    const form = box.closest('form');
    const input = box.querySelector('[data-cv-permit-in]');
    const drop = box.querySelector('[data-cv-permit-drop]');
    const title = box.querySelector('[data-cv-permit-title]');
    const meta = box.querySelector('[data-cv-permit-meta]');
    const go = box.querySelector('[data-cv-permit-go]');
    const state = box.querySelector('[data-cv-permit-state]');
    const url = box.querySelector('[data-cv-permit-url]');
    const t = box.querySelector('[data-cv-permit-t]').dataset;
    const api = box.dataset.api.replace(/\/+$/, '');
    if (!form || !input || !url) return;

    box.hidden = false;
    input.required = true;
    const nojs = form.querySelector('[data-cv-permit-nojs]');
    if (nojs) nojs.hidden = true;

    // The API without file storage: back to "email us the certificate".
    const off = () => {
      box.hidden = true;
      input.required = false;
      input.setCustomValidity('');
      if (nojs) nojs.hidden = false;
    };

    let pending = null;
    let seq = 0;

    const show = (mode, file, note = '') => {
      drop.dataset.state = mode;
      if (mode === 'empty') {
        title.textContent = t.drop;
        meta.textContent = t.types;
        go.textContent = t.browse;
      } else {
        title.textContent = file.name;
        meta.textContent = mode === 'busy' ? `${size(file.size)} · ${t.uploading}` : mode === 'done' ? `${size(file.size)} · ${t.done}` : size(file.size);
        go.textContent = t.change;
      }
      state.textContent = note;
      state.dataset.tone = mode === 'error' ? 'error' : '';
      input.setCustomValidity(mode === 'error' ? note || t.fail : '');
    };

    const take = (file) => {
      url.value = '';
      const n = ++seq;
      if (!file) {
        pending = null;
        return show('empty');
      }
      if (!OK.test(file.name) && !/^(application\/pdf|image\/(jpeg|png|webp|heic|heif))$/.test(file.type)) return show('error', file, t.type);
      if (file.size > MAX) return show('error', file, t.size);
      show('busy', file);
      const fd = new FormData();
      fd.append('file', file, file.name);
      const email = form.querySelector('input[name="contact[email]"]');
      if (email && email.value) fd.append('email', email.value);
      pending = fetch(`${api}/permit`, { method: 'POST', body: fd })
        .then((r) => r.json().then((d) => (r.ok && d.url ? d : Promise.reject(d.error))))
        .then((d) => {
          if (n !== seq) return;
          url.value = d.url;
          show('done', file);
        })
        .catch((err) => {
          if (n !== seq) return;
          if (err === 'off') return off();
          show('error', file, err === 'type' ? t.type : err === 'size' ? t.size : t.fail);
        })
        .finally(() => {
          if (n === seq) pending = null;
        });
    };

    input.addEventListener('change', () => take(input.files && input.files[0]));

    // Drag and drop onto the field: the file is set on the input itself, so
    // the browser's own required check sees it.
    ['dragenter', 'dragover'].forEach((ev) =>
      drop.addEventListener(ev, (e) => {
        e.preventDefault();
        drop.dataset.over = '';
      })
    );
    ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, () => delete drop.dataset.over));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      const files = e.dataTransfer && e.dataTransfer.files;
      if (!files || !files.length) return;
      try {
        input.files = files;
      } catch (err) {
        /* older Safari: the upload still runs */
      }
      take(files[0]);
    });

    // Capture, ahead of Shopify's hCaptcha listener on the form: hold the
    // application until the file is in, then send the store its copy.
    window.addEventListener(
      'submit',
      (e) => {
        if (e.target !== form) return;
        if (box.hidden) return mailCopy(form);
        if (pending || !url.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (pending) {
            // Sent by itself once the file is in.
            state.textContent = t.wait;
            pending.then(() => url.value && form.requestSubmit());
          } else {
            state.textContent = state.textContent || t.need;
            state.dataset.tone = 'error';
            input.focus();
          }
          return;
        }
        mailCopy(form);
      },
      true
    );
  }

  // Every answer, as the note writes it, plus the link: one email to the store.
  function mailCopy(form) {
    const copy = form.parentElement.querySelector('[data-cv-permit-copy]');
    if (!copy) return;
    const fd = new FormData(form);
    const lines = [];
    for (const [k, v] of fd) {
      const m = /^contact\[note\]\[(.+)\]$/.exec(k);
      if (!m || !String(v).trim()) continue;
      lines.push(m[1].startsWith('Practice: ') ? `Practice: ${m[1].slice(10)}` : `${m[1]}: ${v}`);
    }
    copy.querySelector('[name="contact[email]"]').value = fd.get('contact[email]') || '';
    copy.querySelector('[name="contact[name]"]').value = fd.get('contact[first_name]') || '';
    copy.querySelector('[name="contact[body]"]').value = lines.join('\n');
    try {
      fetch(copy.action, { method: 'POST', body: new FormData(copy), credentials: 'same-origin', keepalive: true }).catch(() => {});
    } catch (err) {
      /* the note on the customer record stands */
    }
  }

  const start = () => document.querySelectorAll('[data-cv-permit]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
